import React from 'react';
import { Trophy, Medal, Award } from 'lucide-react';

// Premium Color Palette from PREMIUM_METRIC_PATTERNS.md
const PREMIUM_COLORS = {
  goldAccent: '#F59E0B',
  silverAccent: '#6B7280', 
  bronzeAccent: '#EA580C',
  primaryGolfGreen: '#1B4332',
  neutralGray: '#9CA3AF'
};

export interface LeaderboardRankIconProps {
  rank: number;
  size?: 'sm' | 'md' | 'lg';
  showBackground?: boolean;
  className?: string;
}

/**
 * Unified Rank Icon Component
 * 
 * Consolidates rank display logic used across all leaderboard components:
 * - Trophy for 1st place (gold)
 * - Medal for 2nd place (silver) 
 * - Award for 3rd place (bronze)
 * - Number for 4th+ places
 * - Consistent sizing and colors
 * - Optional background highlighting
 */
export function LeaderboardRankIcon({ 
  rank, 
  size = 'md', 
  showBackground = false,
  className = '' 
}: LeaderboardRankIconProps) {
  
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'w-4 h-4 text-sm';
      case 'lg': return 'w-6 h-6 text-lg';
      default: return 'w-5 h-5 text-base';
    }
  };

  const getBackgroundStyle = () => {
    if (!showBackground) return {};
    
    switch (rank) {
      case 1:
        return {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '50%',
          padding: '8px'
        };
      case 2:
        return {
          backgroundColor: 'rgba(107, 114, 128, 0.1)',
          borderRadius: '50%',
          padding: '8px'
        };
      case 3:
        return {
          backgroundColor: 'rgba(234, 88, 12, 0.1)',
          borderRadius: '50%',
          padding: '8px'
        };
      default:
        return {};
    }
  };

  const iconClasses = getSizeClasses();

  // Render appropriate icon based on rank
  if (rank === 1) {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} style={getBackgroundStyle()}>
        <Trophy className={iconClasses} style={{ color: PREMIUM_COLORS.goldAccent }} />
      </div>
    );
  }

  if (rank === 2) {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} style={getBackgroundStyle()}>
        <Medal className={iconClasses} style={{ color: PREMIUM_COLORS.silverAccent }} />
      </div>
    );
  }

  if (rank === 3) {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} style={getBackgroundStyle()}>
        <Award className={iconClasses} style={{ color: PREMIUM_COLORS.bronzeAccent }} />
      </div>
    );
  }

  // For ranks 4 and above, show the number
  return (
    <div className={`inline-flex items-center justify-center ${className}`} style={getBackgroundStyle()}>
      <span 
        className={`font-semibold ${iconClasses}`}
        style={{ color: PREMIUM_COLORS.primaryGolfGreen }}
      >
        {rank}
      </span>
    </div>
  );
}

/**
 * Utility function to get rank color (for legacy compatibility)
 */
export function getRankColor(rank: number): string {
  switch (rank) {
    case 1: return PREMIUM_COLORS.goldAccent;
    case 2: return PREMIUM_COLORS.silverAccent;
    case 3: return PREMIUM_COLORS.bronzeAccent;
    default: return PREMIUM_COLORS.primaryGolfGreen;
  }
}

/**
 * Utility function to check if rank gets trophy icon
 */
export function isTopThreeRank(rank: number): boolean {
  return rank >= 1 && rank <= 3;
}